import React from 'react';
import { Check, X, Video, Calendar, Bell, Users, BarChart3, Smartphone, Mail, MessageSquare, FileText, Globe, Star } from 'lucide-react';
import { Button } from '../ui/button';

const PricingComparison = ({ onSelectPlan, selectedPlan }) => {
  const plans = [
    {
      id: 'essentiel',
      name: 'Essentiel',
      price: 29,
      description: 'Pour démarrer',
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-700',
      btnColor: 'bg-green-600 hover:bg-green-700',
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 49,
      description: 'Le plus populaire',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-400',
      textColor: 'text-blue-700',
      btnColor: 'bg-blue-600 hover:bg-blue-700',
      popular: true,
    },
    {
      id: 'multi',
      name: 'Multi',
      price: 79,
      description: 'Clinique & équipe',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-700',
      btnColor: 'bg-purple-600 hover:bg-purple-700',
    },
  ];

  const features = [
    {
      category: 'Agenda & Rendez-vous',
      icon: Calendar,
      items: [
        { name: 'Prise de RDV en ligne', essentiel: true, pro: true, multi: true },
        { name: 'Vue agenda jour', essentiel: true, pro: true, multi: true },
        { name: 'Vue semaine & mois', essentiel: false, pro: true, multi: true },
        { name: 'Créneaux récurrents', essentiel: false, pro: true, multi: true },
        { name: 'Gestion des congés/absences', essentiel: false, pro: true, multi: true },
        { name: 'RDV par mois', essentiel: '100', pro: '200', multi: '500' },
      ]
    },
    {
      category: 'Notifications',
      icon: Bell,
      items: [
        { name: 'Rappels par email', essentiel: true, pro: true, multi: true },
        { name: 'Rappels par SMS', essentiel: false, pro: true, multi: true },
        { name: 'Notifications push', essentiel: false, pro: true, multi: true },
      ]
    },
    {
      category: 'Téléconsultation',
      icon: Video,
      highlight: true,
      items: [
        { name: 'Consultation vidéo HD', essentiel: false, pro: true, multi: true },
        { name: 'Chat pendant la consultation', essentiel: false, pro: true, multi: true },
        { name: 'Partage d\'écran', essentiel: false, pro: true, multi: true },
      ]
    },
    {
      category: 'Gestion patients',
      icon: FileText,
      items: [
        { name: 'Fiches patients basiques', essentiel: true, pro: true, multi: true },
        { name: 'Historique complet', essentiel: false, pro: true, multi: true },
        { name: 'Documents & ordonnances', essentiel: false, pro: true, multi: true },
        { name: 'Carnet de santé digital', essentiel: true, pro: true, multi: true },
      ]
    },
    {
      category: 'Équipe & Collaborateurs',
      icon: Users,
      items: [
        { name: 'Nombre de vétérinaires', essentiel: '1', pro: '2', multi: '5' },
        { name: 'Gestion des rôles', essentiel: false, pro: false, multi: true },
        { name: 'Agenda partagé', essentiel: false, pro: true, multi: true },
      ]
    },
    {
      category: 'Outils avancés',
      icon: BarChart3,
      items: [
        { name: 'Statistiques de base', essentiel: true, pro: true, multi: true },
        { name: 'Statistiques avancées', essentiel: false, pro: false, multi: true },
        { name: 'Sync Google Calendar', essentiel: false, pro: false, multi: true },
        { name: 'Export des données', essentiel: false, pro: true, multi: true },
      ]
    },
  ];

  const renderValue = (value, planColor) => {
    if (value === true) {
      return <Check className={`w-5 h-5 text-${planColor}-600`} />;
    } else if (value === false) {
      return <X className="w-5 h-5 text-gray-300" />;
    } else {
      return <span className={`font-semibold text-${planColor}-700`}>{value}</span>;
    }
  };

  return (
    <div className="w-full">
      {/* Header with plans */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="col-span-1"></div>
        {plans.map((plan) => (
          <div 
            key={plan.id}
            className={`relative rounded-2xl p-6 text-center ${plan.bgColor} ${plan.borderColor} border-2 ${
              plan.popular ? 'ring-2 ring-blue-500 ring-offset-2' : ''
            } ${selectedPlan === plan.id ? 'ring-2 ring-offset-2' : ''}`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" /> POPULAIRE
              </div>
            )}
            <h3 className={`text-2xl font-bold ${plan.textColor}`}>{plan.name}</h3>
            <p className="text-gray-500 text-sm mb-3">{plan.description}</p>
            <div className="mb-4">
              <span className={`text-4xl font-bold ${plan.textColor}`}>{plan.price}€</span>
              <span className="text-gray-500">/mois</span>
            </div>
            <Button 
              className={`w-full ${plan.btnColor} text-white`}
              onClick={() => onSelectPlan && onSelectPlan(plan.id)}
              data-testid={`select-plan-${plan.id}`}
            >
              {selectedPlan === plan.id ? 'Sélectionné ✓' : 'Choisir'}
            </Button>
          </div>
        ))}
      </div>

      {/* Feature comparison table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {features.map((category, catIndex) => {
          const CategoryIcon = category.icon;
          return (
            <div key={catIndex} className={category.highlight ? 'bg-blue-50/50' : ''}>
              {/* Category header */}
              <div className={`grid grid-cols-4 gap-4 px-6 py-4 ${
                category.highlight ? 'bg-blue-100/50 border-y border-blue-200' : 'bg-gray-50 border-b border-gray-200'
              }`}>
                <div className="col-span-1 flex items-center gap-2">
                  <CategoryIcon className={`w-5 h-5 ${category.highlight ? 'text-blue-600' : 'text-gray-600'}`} />
                  <span className={`font-semibold ${category.highlight ? 'text-blue-800' : 'text-gray-800'}`}>
                    {category.category}
                  </span>
                  {category.highlight && (
                    <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full ml-2">NOUVEAU</span>
                  )}
                </div>
                {plans.map((plan) => (
                  <div key={plan.id} className="text-center">
                    <span className={`text-sm font-medium ${plan.textColor}`}>{plan.name}</span>
                  </div>
                ))}
              </div>
              
              {/* Category items */}
              {category.items.map((item, itemIndex) => (
                <div 
                  key={itemIndex} 
                  className={`grid grid-cols-4 gap-4 px-6 py-3 ${
                    itemIndex !== category.items.length - 1 ? 'border-b border-gray-100' : ''
                  } hover:bg-gray-50 transition-colors`}
                >
                  <div className="col-span-1 text-gray-700">{item.name}</div>
                  <div className="text-center flex justify-center">
                    {renderValue(item.essentiel, 'green')}
                  </div>
                  <div className="text-center flex justify-center">
                    {renderValue(item.pro, 'blue')}
                  </div>
                  <div className="text-center flex justify-center">
                    {renderValue(item.multi, 'purple')}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Trial info */}
      <div className="mt-6 text-center bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
        <p className="text-lg font-semibold text-gray-800 mb-2">
          🎁 60 jours d'essai gratuit sur tous les forfaits
        </p>
        <p className="text-gray-600">
          Testez toutes les fonctionnalités sans engagement. Premier paiement après la période d'essai.
        </p>
      </div>
    </div>
  );
};

export default PricingComparison;
